import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const ROW_HEIGHT = 30;
const AXIS_SPACE = 56; // x-axis ticks + x-axis title

// Horizontal bar chart shared by Dashboard and Reports.
// Auto-scales: height grows with the number of rows, the label column
// fits the longest name, and the value axis rounds up to whole numbers.
export default function HBarChart({
  data,
  xLabel = "Number of tickets",
  yLabel,
  color = "#2563eb",
  tooltip,
  onBarClick,
  minHeight = 180,
}) {
  const longest = data.reduce((max, d) => Math.max(max, String(d.name ?? "").length), 0);
  const labelWidth = Math.min(220, Math.max(70, longest * 6.6 + 14));
  const height = Math.max(minHeight, data.length * ROW_HEIGHT + AXIS_SPACE);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 4, right: 40, bottom: 20, left: yLabel ? 18 : 0 }}
        barCategoryGap={6}
      >
        <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          type="number"
          allowDecimals={false}
          domain={[0, (dataMax) => Math.max(1, Math.ceil(dataMax * 1.1))]}
          tickCount={6}
          fontSize={11}
          tick={{ fill: "#64748b" }}
          tickLine={false}
          axisLine={{ stroke: "#cbd5e1" }}
          label={{
            value: xLabel,
            position: "insideBottom",
            offset: -14,
            fill: "#475569",
            fontSize: 11,
            fontWeight: 600,
          }}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={labelWidth}
          interval={0}
          fontSize={12}
          tick={{ fill: "#475569" }}
          tickLine={false}
          axisLine={{ stroke: "#cbd5e1" }}
          label={
            yLabel
              ? {
                  value: yLabel,
                  angle: -90,
                  position: "left",
                  offset: 4,
                  fill: "#475569",
                  fontSize: 11,
                  fontWeight: 600,
                  style: { textAnchor: "middle" },
                }
              : undefined
          }
        />
        <Tooltip cursor={{ fill: "#f1f5f9" }} content={tooltip} />
        <Bar
          dataKey="value"
          radius={[0, 4, 4, 0]}
          maxBarSize={18}
          fill={color}
          activeBar={{ fillOpacity: 0.8 }}
          // Hand callers their own row, whatever shape recharts passes.
          onClick={onBarClick ? (bar, index) => onBarClick(data[index] ?? bar?.payload ?? bar) : undefined}
          cursor={onBarClick ? "pointer" : undefined}
        >
          {data.map((entry, index) => (
            <Cell key={`${entry.name}-${index}`} fill={entry.color || color} />
          ))}
          <LabelList dataKey="value" position="right" fontSize={11} fill="#334155" />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
